import { safePnl } from "../lib/security";

const PROP_FIRMS = {
  lucid: {
    name: "Lucid Trading",
    icon: "💎",
    plans: {
      flex: {
        name: "LucidFlex",
        sizes: [25000, 50000, 100000, 150000],
        profitSplit: 0.9,
        minDailyProfit: {
          25000: 100,
          50000: 150,
          100000: 200,
          150000: 250,
        },
        profitableDaysRequired: 5,
        minPayout: 500,
        maxPayout: {
          25000: 1000,
          50000: 2000,
          100000: 2500,
          150000: 3000,
        },
        maxPayoutPct: 0.5,
        buffer: 0,
        maxPayouts: 6,
        payoutFrequency: null,
        consistencyRule: "profitable_days",
      },
      pro: {
        name: "LucidPro",
        sizes: [25000, 50000, 100000, 150000],
        profitSplit: 0.9,
        minDailyProfit: {},
        minProfitGoal: {
          25000: 250,
          50000: 500,
          100000: 750,
          150000: 1000,
        },
        profitableDaysRequired: 0,
        minPayout: 500,
        maxPayout: {
          25000: 1500,
          50000: 2500,
          100000: 3000,
          150000: 3500,
        },
        maxPayoutFirst: {
          25000: 1000,
          50000: 2000,
          100000: 2500,
          150000: 3000,
        },
        maxPayoutPct: 1.0,
        buffer: {
          25000: 1100,
          50000: 2100,
          100000: 3100,
          150000: 4600,
        },
        maxPayouts: null,
        payoutFrequency: null,
        consistencyRule: "forty_percent",
      },
    },
  },
  mffu: {
    name: "MyFundedFutures",
    icon: "🚀",
    plans: {
      rapid: {
        name: "Rapid",
        sizes: [50000, 100000, 150000],
        profitSplit: 0.9,
        minDailyProfit: {},
        profitableDaysRequired: 0,
        minPayout: 500,
        maxPayout: {},
        maxPayoutPct: 1.0,
        buffer: {
          50000: 2100,
          100000: 3100,
          150000: 4600,
        },
        maxPayouts: null,
        payoutFrequency: "daily",
        firstPayoutAfterHours: 24,
        consistencyRule: "none",
      },
      flex: {
        name: "Flex",
        sizes: [25000, 50000],
        profitSplit: 0.8,
        minDailyProfit: {
          25000: 100,
          50000: 150,
        },
        profitableDaysRequired: 5,
        minPayout: 250,
        maxPayout: {
          25000: 3000,
          50000: 5000,
        },
        maxPayoutPct: 0.5,
        buffer: 0,
        maxPayouts: null,
        payoutFrequency: null,
        consistencyRule: "winning_days",
      },
      pro: {
        name: "Pro",
        sizes: [50000, 100000, 150000],
        profitSplit: 0.8,
        minDailyProfit: {},
        profitableDaysRequired: 0,
        minPayout: 1000,
        maxPayout: {
          50000: 100000,
          100000: 100000,
          150000: 100000,
        },
        maxPayoutPct: 1.0,
        buffer: {
          50000: 2100,
          100000: 3100,
          150000: 4600,
        },
        bufferWithdrawPct: 0.6,
        maxPayouts: null,
        payoutFrequency: 14,
        consistencyRule: "none",
      },
    },
  },
  topstep: {
    name: "Topstep",
    icon: "📈",
    plans: {
      express: {
        name: "Express Standard",
        sizes: [50000, 100000, 150000],
        profitSplit: 0.9,
        minDailyProfit: { default: 150 },
        profitableDaysRequired: 5,
        minPayout: 0.01,
        maxPayout: { default: 5000 },
        maxPayoutPct: 0.5,
        buffer: 0,
        maxPayouts: null,
        payoutFrequency: null,
        consistencyRule: "profitable_since_last",
      },
      consistency: {
        name: "Express Consistency",
        sizes: [50000, 100000, 150000],
        profitSplit: 0.9,
        minDailyProfit: {},
        profitableDaysRequired: 3,
        minPayout: 0.01,
        maxPayout: { default: 6000 },
        maxPayoutPct: 0.5,
        buffer: 0,
        maxPayouts: null,
        payoutFrequency: null,
        consistencyRule: "forty_percent",
      },
    },
  },
  alpha: {
    name: "Alpha Futures",
    icon: "⚡",
    plans: {
      standard: {
        name: "Standard",
        sizes: [50000, 100000, 150000],
        tieredSplit: { 0: 0.7, 2: 0.8, 4: 0.9 },
        profitSplit: 0.7,
        minDailyProfit: {},
        profitableDaysRequired: 0,
        minPayout: 200,
        maxPayout: { default: 15000 },
        maxPayoutPct: 1.0,
        buffer: 0,
        maxPayouts: null,
        payoutFrequency: 14,
        consistencyRule: "forty_percent",
      },
      advanced: {
        name: "Advanced",
        sizes: [50000, 100000, 150000],
        profitSplit: 0.9,
        minDailyProfit: { default: 200 },
        profitableDaysRequired: 5,
        minPayout: 1000,
        maxPayout: { default: 15000 },
        maxPayoutPct: 0.5,
        fullPayoutAfterDays: 30,
        buffer: 0,
        maxPayouts: null,
        payoutFrequency: null,
        consistencyRule: "none",
      },
      zero: {
        name: "Zero",
        sizes: [50000, 100000],
        profitSplit: 0.9,
        minDailyProfit: { default: 200 },
        profitableDaysRequired: 5,
        minPayout: 200,
        maxPayout: {
          50000: 1500,
          100000: 3000,
        },
        maxPayoutPct: 0.5,
        fullPayoutAfterDays: 30,
        buffer: 0,
        maxPayouts: null,
        payoutFrequency: null,
        consistencyRule: "forty_percent",
      },
    },
  },
};

function computePropStatus(account, breakdowns) {
  const emptyResult = {
    checks: [],
    eligible: false,
    availablePayout: 0,
    currentBalance: 0,
    totalPnl: 0,
    winningDays: 0,
    firmName: "?",
    planName: "?",
    firmIcon: "?",
    payoutsTaken: 0,
    trades: [],
    winningDaysRemaining: null,
    calendarDaysRemaining: null,
    consistencyTarget: null,
    consistencyHighestDay: null,
  };
  const firm = PROP_FIRMS[account.firm];
  if (!firm) return emptyResult;
  const plan = firm.plans[account.plan];
  if (!plan) return { ...emptyResult, firmName: firm.name, firmIcon: firm.icon };
  const size = account.accountSize;
  const trades = (breakdowns || []).filter(
    (b) =>
      (b.propAccountIds && b.propAccountIds.includes(account.id)) ||
      b.propAccountId === account.id
  );
  const payouts = account.payouts || [];

  const dailyMap = {};
  let totalPnl = 0;
  trades.forEach((t) => {
    if (!t.date) return;
    const p = safePnl(t.pnl);
    totalPnl += p;
    dailyMap[t.date] = (dailyMap[t.date] || 0) + p;
  });
  const totalPayoutGross = payouts.reduce(
    (s, p) => s + (p.amount || 0),
    0
  );
  const currentBalance = size + totalPnl - totalPayoutGross;
  const netProfit = totalPnl - totalPayoutGross;

  const lastPayout = payouts.length > 0 ? payouts[payouts.length - 1] : null;
  let pnlSinceLastPayout = totalPnl;
  if (lastPayout) {
    pnlSinceLastPayout = trades
      .filter(
        (t) =>
          t.date > lastPayout.date ||
          (t.date === lastPayout.date && t.id > lastPayout.id)
      )
      .reduce((s, t) => s + safePnl(t.pnl), 0);
  }

  const minDaily =
    plan.minDailyProfit[size] || plan.minDailyProfit?.default || 0;
  const winningDays = Object.entries(dailyMap).filter(
    ([, pnl]) => pnl >= (minDaily || 0.01)
  ).length;

  const bufferRequired =
    typeof plan.buffer === "object"
      ? plan.buffer[size] || 0
      : plan.buffer || 0;
  const bufferMet = bufferRequired === 0 || netProfit >= bufferRequired;

  const sortedDates = trades
    .map((t) => t.date)
    .filter(Boolean)
    .sort();
  const firstTradeDate = sortedDates.length > 0 ? sortedDates[0] : null;
  let daysSinceFirstTrade = 0;
  if (firstTradeDate)
    daysSinceFirstTrade = Math.floor(
      (Date.now() - new Date(firstTradeDate).getTime()) / 86400000
    );

  const dailyPnls = Object.values(dailyMap);
  const highestDay = dailyPnls.length > 0 ? Math.max(...dailyPnls) : 0;
  const consistencyTarget =
    highestDay > 0
      ? Math.round((highestDay / 0.4) * 100) / 100
      : 0;

  let effectiveSplit = plan.profitSplit;
  if (plan.tieredSplit) {
    const payoutCount = payouts.length;
    const tiers = Object.entries(plan.tieredSplit).sort(
      (a, b) => Number(b[0]) - Number(a[0])
    );
    for (const [threshold, split] of tiers) {
      if (payoutCount >= Number(threshold)) {
        effectiveSplit = split;
        break;
      }
    }
  }

  let effectiveMaxPct = plan.maxPayoutPct || 1.0;
  if (plan.fullPayoutAfterDays && winningDays >= plan.fullPayoutAfterDays) {
    effectiveMaxPct = 1.0;
  }

  const checks = [];
  let eligible = true;

  if (plan.profitableDaysRequired > 0) {
    const met = winningDays >= plan.profitableDaysRequired;
    checks.push({
      label: `Winning Days${
        minDaily > 0 ? ` ($${minDaily}+ each)` : ""
      }`,
      current: winningDays,
      target: plan.profitableDaysRequired,
      met,
    });
    if (!met) eligible = false;
  }

  if (bufferRequired > 0) {
    const met = bufferMet;
    checks.push({
      label: "Buffer Target",
      current: Math.max(0, Math.round(netProfit * 100) / 100),
      target: bufferRequired,
      met,
      isBuffer: true,
      isCurrency: true,
    });
    if (!met && !(plan.bufferWithdrawPct > 0)) eligible = false;
  }

  if (plan.consistencyRule === "forty_percent") {
    const met =
      totalPnl > 0 &&
      (highestDay <= 0 || totalPnl >= consistencyTarget);
    checks.push({
      label: "40% Consistency",
      current: Math.round(totalPnl * 100) / 100,
      target: consistencyTarget,
      met,
      isCurrency: true,
      isConsistency: true,
      highestDay: Math.round(highestDay * 100) / 100,
    });
    if (!met && totalPnl > 0) eligible = false;
  }

  if (plan.consistencyRule === "profitable_since_last" && payouts.length > 0) {
    const met = pnlSinceLastPayout > 0;
    checks.push({
      label: "Profitable Since Last Payout",
      current: Math.round(pnlSinceLastPayout * 100) / 100,
      target: 0.01,
      met,
      isCurrency: true,
    });
    if (!met && pnlSinceLastPayout !== 0) eligible = false;
  }

  if (plan.consistencyRule === "winning_days") {
    const met = winningDays >= plan.profitableDaysRequired;
    checks.push({
      label: "Winning Days Consistency",
      current: winningDays,
      target: plan.profitableDaysRequired,
      met,
    });
    if (!met) eligible = false;
  }

  const grossPayoutCap = plan.maxPayout[size] ?? plan.maxPayout?.default ?? 0;
  const maxPayoutAbsolute = grossPayoutCap * effectiveMaxPct;
  const eligiblePnl = Math.max(0, totalPnl);
  const eligibleAmount = Math.min(eligiblePnl, maxPayoutAbsolute);
  const availablePayoutRaw = eligibleAmount * effectiveSplit;

  const payoutsTaken = payouts.length;
  const totalPaidOut = payouts.reduce(
    (s, p) => s + (p.amount || 0),
    0
  );
  const remainingPayoutRoom = Math.max(
    0,
    maxPayoutAbsolute - totalPaidOut
  );
  const availablePayout = Math.min(
    availablePayoutRaw,
    remainingPayoutRoom
  );

  if (plan.maxPayouts && payoutsTaken >= plan.maxPayouts) {
    checks.push({
      label: "Max Payouts Reached",
      current: payoutsTaken,
      target: plan.maxPayouts,
      met: false,
    });
    eligible = false;
  }

  let winningDaysRemaining = null;
  if (plan.profitableDaysRequired > 0) {
    winningDaysRemaining = Math.max(
      0,
      plan.profitableDaysRequired - winningDays
    );
  }

  let calendarDaysRemaining = null;
  if (plan.payoutFrequency && firstTradeDate) {
    const freqDays = plan.payoutFrequency;
    const remainder = daysSinceFirstTrade % freqDays;
    calendarDaysRemaining =
      remainder === 0 ? 0 : freqDays - remainder;
  }

  return {
    checks,
    eligible,
    availablePayout: Math.round(availablePayout * 100) / 100,
    currentBalance: Math.round(currentBalance * 100) / 100,
    totalPnl: Math.round(totalPnl * 100) / 100,
    netProfit: Math.round(netProfit * 100) / 100,
    winningDays,
    totalDays: Object.keys(dailyMap).length,
    firmName: firm.name,
    planName: plan.name,
    firmIcon: firm.icon,
    payoutsTaken,
    trades,
    winningDaysRemaining,
    calendarDaysRemaining,
    consistencyTarget,
    consistencyHighestDay: Math.round(highestDay * 100) / 100,
    fullPayoutUnlocked: plan.fullPayoutAfterDays ? winningDays >= plan.fullPayoutAfterDays : false,
    fullPayoutDaysRemaining: plan.fullPayoutAfterDays ? Math.max(0, plan.fullPayoutAfterDays - winningDays) : null,
  };
}

export { PROP_FIRMS, computePropStatus };

